/**
 * The headings of a document in order, and the path each paragraph sits under:
 * `["Main Agreement", "4. Payment", "4.2 Invoicing"]` for a paragraph in clause
 * 4.2 of the main body, `["Exhibit A", "1. SUPPORT"]` for one in an exhibit.
 *
 * This is the one home of the heading rules. The reader (`OoxmlRange.documentParagraphs`)
 * writes each paragraph's `headingPath` from it, and every reader of that list
 * (the add-in's recovery of a lost card, the web's paragraph locator, the core's
 * card anchors) takes the path from the paragraph rather than deriving its own.
 *
 * WHAT COUNTS AS A HEADING, by the most authoritative signal that answers
 *   1. the line opens a part (`Exhibit A`, `Schedule 2 - Data Processing`)   -> part heading, depth 0
 *   2. Word states an outline level (a `Heading 1` style, `w:outlineLvl`)     -> that level plus one
 *   3. a dotted clause number is typed in (`4.2 Payment Terms`)              -> one depth per segment
 *   4. Word paints a dotted numbering label (`4.2` from an auto-numbered list) -> one depth per segment
 *   5. the line names a clause division (`ARTICLE 1 - DEFINITIONS`)           -> depth 1
 *   6. anything else                                                           -> not a heading
 *
 * A list level alone never makes a heading. A bullet or a numbered item is a list
 * level too, and a document whose "Expected" bullets re-seated its `Heading 1`
 * gave every card under them a path naming a bullet (2026-10-16, PR #2238 fix
 * check). An auto-numbered contract still gets its clauses from the painted
 * dotted label (rule 4); a bullet paints no dotted label and so names nothing.
 *
 * A paragraph inside a table cell is part of the table's content, never a
 * division of the document, however it is numbered.
 */
import type { DocumentParagraph } from "../wire/document-paragraph";

/** The part every contract starts in, before any exhibit or schedule heading. */
export const MAIN_PART_NAME = "Main Agreement";

/**
 * A part heading (`Exhibit A`) sits beside the clause depths rather than in them:
 * it resets every clause depth instead of taking one.
 */
export const PART_DEPTH = 0;

/** The depth of a top-level clause (`4.`, `ARTICLE 1`, a `Heading 1` style). */
export const FIRST_CLAUSE_DEPTH = 1;

/** `w:outlineLvl` numbers heading levels 0-8; 9 is Word's "body text", not a heading. */
export const BODY_TEXT_OUTLINE_LEVEL = 9;

/**
 * A line opening a new part of the contract, with or without a title after the
 * designation: `Exhibit A`, `Schedule 2`, `Annex III - Data Processing`.
 */
const PART_DIVISION_PATTERN = /^(?:Exhibit|Schedule|Addendum|Attachment|Appendix|Annex)\s+(?:\d+|[A-Z]|[IVXLCDM]+)\b/i;

/**
 * A line opening a top-level clause by name rather than by number:
 * `ARTICLE 1 - DEFINITIONS`, `Section 5 Liability`, `PART II`.
 */
const CLAUSE_DIVISION_PATTERN = /^(?:Article|Section|Part|Clause)\s+(?:\d+|[A-Z]|[IVXLCDM]+)\b/i;

/**
 * The dotted-decimal part of a clause number, however it is closed: `4`, `4.`,
 * `4.2`, `4.2.1)`. Only digits count as depth-bearing segments: `(a)` and `iv.`
 * number items inside a clause, never the clause itself, so they name no heading.
 */
const DOTTED_CLAUSE_NUMBER_PATTERN = /^(\d+(?:\.\d+)*)/;

/** What the heading rules read off a paragraph; every reader paragraph satisfies it. */
export type HeadingSignalSource = Pick<
    DocumentParagraph,
    "paragraphIndex" | "allChangesAcceptedText" | "leadingClauseMarker" | "numberingLabel" | "outlineLevel" | "tableCellPath"
>;

/**
 * One heading found in the document: where it sits, the part it belongs to, how
 * deep it nests, the numbering it carries and the heading text without that
 * numbering.
 *
 * `depth` is `PART_DEPTH` for a part heading and `FIRST_CLAUSE_DEPTH` or more for
 * a clause heading; it is not capped, so `4.2.1.3` is depth four.
 */
export interface HeadingEntry {
    paragraphIndex: number;
    part: string;
    depth: number;
    numbering: string;
    text: string;
}

/**
 * What one paragraph turned out to be: a heading at `depth`, carrying `numbering`
 * (empty when the heading is named rather than numbered) and the heading text with
 * that numbering stripped off the front. A paragraph that is no heading produces
 * no signal at all, so this always describes a heading.
 */
interface HeadingSignal {
    depth: number;
    numbering: string;
    text: string;
}

/**
 * The path segment of one heading.
 *
 * CASES
 *   - a part heading                     -> the part name alone ("Exhibit A")
 *   - a clause named rather than numbered -> the line itself ("ARTICLE 1 - DEFINITIONS")
 *   - a numbered clause                  -> `numbering text` ("4.2 Payment Terms")
 */
export function headingLabelOf(entry: HeadingEntry): string {
    if (entry.depth === PART_DEPTH) return entry.text;
    if (entry.numbering.length === 0) return entry.text;
    return `${entry.numbering} ${entry.text}`;
}

/** The headings of a document in order, and the path each paragraph sits under. */
export class DocumentHeadings {
    readonly entries: HeadingEntry[];
    private readonly paragraphCount: number;

    constructor(paragraphs: HeadingSignalSource[]) {
        this.paragraphCount = paragraphs.length;
        this.entries = DocumentHeadings.extract(paragraphs);
    }

    /**
     * One path per paragraph, index-aligned with the paragraphs the class was built from.
     *
     * Walks the paragraphs and the headings together: a heading takes effect at its
     * own paragraph (`paragraphIndex <= index`), so a heading's path is its own path,
     * and it stays in effect until the next heading at its depth or shallower.
     *
     * CASES, for a heading reached
     *   - part heading   -> a new part, every clause depth cleared
     *   - clause heading -> it replaces whatever stood at its depth, and every deeper heading is dropped
     *
     * NULL
     *   Nothing structural has been reached yet: no part heading and no clause heading
     *   stands over this paragraph, which is what a title page, the parties block and
     *   the recitals look like.
     */
    paths(): (string[] | null)[] {
        const paths: (string[] | null)[] = [];
        let partName = MAIN_PART_NAME;
        let aPartHeadingWasReached = false;
        let headingsInEffect: HeadingEntry[] = [];
        /** The next heading not yet reached; every heading before it already took effect. */
        let cursor = 0;
        for (let index = 0; index < this.paragraphCount; index += 1) {
            while (cursor < this.entries.length && this.entries[cursor].paragraphIndex <= index) {
                const entry = this.entries[cursor];
                if (entry.depth === PART_DEPTH) {
                    partName = entry.text;
                    aPartHeadingWasReached = true;
                    headingsInEffect = [];
                } else {
                    headingsInEffect = DocumentHeadings.reseated(headingsInEffect, entry);
                }
                cursor += 1;
            }
            paths.push(DocumentHeadings.pathFrom(partName, aPartHeadingWasReached, headingsInEffect));
        }
        return paths;
    }

    /**
     * The headings still in effect once `entry` is reached: everything shallower
     * than it, then `entry` itself.
     *
     * EXAMPLE
     *   headingsInEffect = [4. Payment, 4.2 Invoicing]   (depths 1 and 2)
     *   entry            = 4.3 Disputes                  (depth 2)
     *   kept             = headingsInEffect.slice(0, 2 - 1) -> [4. Payment]
     *   result           = [4. Payment, 4.3 Disputes]
     *
     * EDGE CASE
     *   A document that skips a depth (`4.` straight to `4.2.1`, depth 3 over a
     *   one-deep stack) keeps the one heading it has and seats the new one right
     *   after it, so the path shortens by the skipped level rather than growing a hole.
     */
    private static reseated(headingsInEffect: HeadingEntry[], entry: HeadingEntry): HeadingEntry[] {
        /** Headings shallower than the entry: depth 1 keeps none, depth 2 keeps the first, and so on. */
        const kept = headingsInEffect.slice(0, entry.depth - FIRST_CLAUSE_DEPTH);
        return [...kept, entry];
    }

    /**
     * Every heading in document order, each tagged with the part it falls in.
     *
     * EXAMPLE
     *   allChangesAcceptedText = "4.2 Payment Terms"
     *   leadingClauseMarker    = "4.2"
     *   depth                  = 2 (two dotted segments)
     *   text                   = "4.2 Payment Terms".slice("4.2".length).trim() -> "Payment Terms"
     */
    private static extract(paragraphs: HeadingSignalSource[]): HeadingEntry[] {
        const entries: HeadingEntry[] = [];
        let partName = MAIN_PART_NAME;
        for (const paragraph of paragraphs) {
            const signal = DocumentHeadings.signalFor(paragraph);
            if (signal === null) continue;
            if (signal.depth === PART_DEPTH) partName = signal.text;
            entries.push({ paragraphIndex: paragraph.paragraphIndex, part: partName, depth: signal.depth, numbering: signal.numbering, text: signal.text });
        }
        return entries;
    }

    /**
     * What this paragraph is, by the most authoritative signal that answers.
     *
     * CASES, tested in this order
     *   - it sits inside a table cell            -> no heading: a numbered cell must not reset
     *                                               the heading path of the document around the table
     *   - the line opens a part                  -> part heading, numbering cleared
     *   - Word states an outline level           -> that level plus one
     *   - a dotted clause marker is typed in     -> one depth per dotted segment
     *   - Word paints a dotted numbering label   -> one depth per dotted segment
     *   - the line names a clause division       -> the first clause depth
     *   - anything else                          -> not a heading
     *
     * NULL
     *   The paragraph names no heading; it is body text under whatever stands above it.
     */
    private static signalFor(paragraph: HeadingSignalSource): HeadingSignal | null {
        const text = paragraph.allChangesAcceptedText.trim();
        if (paragraph.tableCellPath.length > 0) return null;
        if (PART_DIVISION_PATTERN.test(text)) return { depth: PART_DEPTH, numbering: "", text };
        const typedNumbering = (paragraph.leadingClauseMarker ?? "").trim();
        const paintedNumbering = (paragraph.numberingLabel ?? "").trim();
        const depth = DocumentHeadings.outlineDepth(paragraph) ?? DocumentHeadings.dottedDepth(typedNumbering) ?? DocumentHeadings.dottedDepth(paintedNumbering);
        if (depth === null && CLAUSE_DIVISION_PATTERN.test(text)) return { depth: FIRST_CLAUSE_DEPTH, numbering: "", text };
        if (depth === null) return null;
        /**
         * The numbering is only stripped when the text really starts with it; a marker
         * the reader trimmed differently, or a label Word paints outside the text
         * altogether, leaves the text whole rather than cut mid-word.
         */
        const numbering = typedNumbering.length > 0 && text.startsWith(typedNumbering) ? typedNumbering : "";
        return { depth, numbering, text: text.slice(numbering.length).trim() };
    }

    /**
     * The depth Word itself states for the paragraph, from its outline level.
     *
     * EXAMPLE
     *   outlineLevel = 0 (a `Heading 1` style) -> depth 0 + 1 = 1
     *   outlineLevel = 1 (a `Heading 2` style) -> depth 1 + 1 = 2
     *
     * NULL
     *   Word states no outline level below body text: the paragraph carries no heading
     *   style, so its depth has to come from the text or the painted numbering instead.
     *   A list level is not consulted: bullets and numbered items are list levels too.
     */
    private static outlineDepth(paragraph: HeadingSignalSource): number | null {
        const outlineLevel = paragraph.outlineLevel;
        if (outlineLevel !== null && outlineLevel < BODY_TEXT_OUTLINE_LEVEL) return outlineLevel + FIRST_CLAUSE_DEPTH;
        return null;
    }

    /**
     * One depth per dotted segment of a clause number.
     *
     * EXAMPLE
     *   "4."     -> segments ["4"]           -> depth 1
     *   "4.2"    -> segments ["4", "2"]      -> depth 2
     *   "4.2.1)" -> segments ["4", "2", "1"] -> depth 3
     *
     * NULL
     *   The numbering does not start with a dotted decimal. `(a)`, `iv.` and `B)`
     *   number items inside a clause rather than the clause itself, so they name no
     *   heading and leave the path they sit under alone.
     */
    private static dottedDepth(numbering: string): number | null {
        const dotted = DOTTED_CLAUSE_NUMBER_PATTERN.exec(numbering);
        if (dotted === null) return null;
        return dotted[1].split(".").length;
    }

    /**
     * `[part, …the headings in effect, shallowest first…]`.
     *
     * CASES
     *   - headings stand over the paragraph  -> the part followed by each of them
     *   - none, but a part heading was read  -> the part alone: everything after `Exhibit A`
     *                                           is at least in Exhibit A, and losing that is
     *                                           worse than a short path
     *   - none, and none was read            -> NULL
     *
     * NULL
     *   The paragraph sits before any structure at all, in the implicit main part: a
     *   title, the parties, a recital. Nothing is known about where it belongs.
     */
    private static pathFrom(partName: string, aPartHeadingWasReached: boolean, headingsInEffect: HeadingEntry[]): string[] | null {
        if (headingsInEffect.length === 0 && !aPartHeadingWasReached) return null;
        return [partName, ...headingsInEffect.map(headingLabelOf)];
    }
}
